import { IParameter } from '@/modules/gpt-chats/types/tools.service.type';
import { ParameterFormField } from './tree-utils';

interface ReconstructOptions {
  forceBodyLocation?: boolean;
}

export const reconstructTree = (
  params: Record<string, IParameter>,
  options: ReconstructOptions = {}
): ParameterFormField[] => {
  return Object.entries(params).map(([key, param]) => {
    const id = Math.random().toString(36).substr(2, 9);
    const isArray = param.Type === 'array';
    const isObject = param.Type === 'object';

    let children: ParameterFormField[] | undefined = undefined;

    if (isObject && param.Properties) {
      children = reconstructTree(param.Properties, options);
    } else if (isArray && param.Items) {
      const itemId = Math.random().toString(36).substr(2, 9);
      const itemParam = param.Items;
      let itemChildren: ParameterFormField[] | undefined = undefined;

      if (itemParam.Type === 'object' && itemParam.Properties) {
        itemChildren = reconstructTree(itemParam.Properties, options);
      }

      children = [
        {
          id: itemId,
          name: '[ITEM]',
          description: itemParam.Description || '',
          type: itemParam.Type,
          location: options.forceBodyLocation
            ? 'body'
            : itemParam.Location || param.Location || 'body',
          defaultValue:
            itemParam.DefaultValue !== undefined && itemParam.DefaultValue !== null
              ? String(itemParam.DefaultValue)
              : '',
          required: itemParam.Required || false,
          children: itemChildren,
          isExpanded: true,
        },
      ];
    }

    return {
      id,
      name: param.Name || key,
      description: param.Description || '',
      type: param.Type,
      location: options.forceBodyLocation ? 'body' : param.Location || 'body',
      defaultValue:
        param.DefaultValue !== undefined && param.DefaultValue !== null
          ? String(param.DefaultValue)
          : '',
      required: param.Required || false,
      children,
      isExpanded: true,
    };
  });
};

export const buildParameters = (
  params: ParameterFormField[],
  ignoreDefaultValues = false
): Record<string, IParameter> => {
  const result: Record<string, IParameter> = {};

  params.forEach((param) => {
    if (param.name) {
      result[param.name] = constructParameterNode(param, false, ignoreDefaultValues);
    }
  });

  return result;
};

const constructParameterNode = (
  param: ParameterFormField,
  isItemChild = false,
  ignoreDefaultValues = false
): IParameter => {
  let defaultValue: any = param.defaultValue;

  if (ignoreDefaultValues || isItemChild) {
    if (ignoreDefaultValues) defaultValue = undefined;
  }

  if (!ignoreDefaultValues && defaultValue && typeof defaultValue === 'string') {
    const type = param.type?.toLowerCase();
    if (type !== 'variable') {
      try {
        const parsed = JSON.parse(defaultValue);
        if (typeof parsed === 'object' && parsed !== null) {
          defaultValue = parsed;
        } else if (type === 'integer' || type === 'number') {
          if (typeof parsed === 'number') defaultValue = parsed;
        } else if (type === 'boolean') {
          if (typeof parsed === 'boolean') defaultValue = parsed;
        }
      } catch (e) {
        // keep string
      }
    }
  }

  const node: IParameter = {
    Name: isItemChild ? '' : param.name,
    Type: param.type,
    Description: param.description,
    Required: param.required,
    Location: (param.location as any) || 'body',
    DefaultValue: defaultValue,
    Properties: undefined,
    Items: undefined,
  };

  if (param.type === 'object' && param.children && param.children.length > 0) {
    node.Properties = buildParameters(param.children, ignoreDefaultValues);
  }

  if (param.type === 'array' && param.children && param.children.length > 0) {
    const itemChild = param.children[0];

    if (!ignoreDefaultValues) {
      const childDef = itemChild.defaultValue;
      let liftedDefault: any = undefined;
      if (childDef) {
        try {
          liftedDefault = JSON.parse(childDef);
          if (typeof liftedDefault !== 'object' || liftedDefault === null) {
            liftedDefault = childDef;
          }
        } catch (e) {
          liftedDefault = childDef;
        }
      }
      if (liftedDefault !== undefined) node.DefaultValue = liftedDefault;
    }
    node.Items = constructParameterNode(itemChild, true, ignoreDefaultValues);
  }

  return node;
};
